// Messaging + tasks + notifications API surface (apps/app). Wraps /messages/*,
// /tasks/*, /notifications/*. docs/replan-v2.md R10.

import { api } from './api';

// ─── Messaging ───────────────────────────────────────────────────────────────

export interface ConversationSummary {
  id: string;
  kind: '1to1' | 'class_broadcast';
  title: string;
  classId: string | null;
  lastMessageAt: string | null;
  lastMessagePreview: string | null;
  unreadCount: number;
  observer: boolean;
}

export interface Message {
  id: string;
  conversationId: string;
  senderUserId: string;
  senderName: string;
  body: string;
  mine: boolean;
  moderationStatus: 'clean' | 'flagged' | 'approved' | 'blocked';
  createdAt: string;
}

export async function listConversations(signal?: AbortSignal): Promise<ConversationSummary[]> {
  const { conversations } = await api<{ conversations: ConversationSummary[] }>('/messages', {
    signal,
  });
  return conversations;
}

export async function getMessages(
  conversationId: string,
  signal?: AbortSignal,
): Promise<Message[]> {
  const { messages } = await api<{ messages: Message[] }>(`/messages/${conversationId}`, {
    signal,
  });
  return messages;
}

export async function markConversationRead(conversationId: string): Promise<void> {
  await api(`/messages/${conversationId}/read`, { method: 'POST' });
}

export async function sendDirectMessage(
  studentUserId: string,
  body: string,
): Promise<{ conversationId: string; message: Message }> {
  return api<{ conversationId: string; message: Message }>('/messages/direct', {
    method: 'POST',
    body: { studentUserId, body },
  });
}

export async function replyToConversation(
  conversationId: string,
  body: string,
): Promise<{ conversationId: string; message: Message }> {
  return api<{ conversationId: string; message: Message }>(`/messages/${conversationId}/reply`, {
    method: 'POST',
    body: { body },
  });
}

export async function broadcastToClass(
  classId: string,
  body: string,
): Promise<{ conversationId: string; message: Message }> {
  return api<{ conversationId: string; message: Message }>('/messages/broadcast', {
    method: 'POST',
    body: { classId, body },
  });
}

// ─── Tasks ───────────────────────────────────────────────────────────────────

export type TaskStatus = 'open' | 'in_progress' | 'done' | 'verified';

export interface Task {
  id: string;
  title: string;
  status: TaskStatus;
  assigneeUserId: string;
  assigneeName: string | null;
  createdByUserId: string;
  dueAt: string | null;
  completedAt: string | null;
  createdAt: string;
}

export async function listCreatedTasks(signal?: AbortSignal): Promise<Task[]> {
  const { tasks } = await api<{ tasks: Task[] }>('/tasks/assigned', { signal });
  return tasks;
}

export async function listMyTasks(signal?: AbortSignal): Promise<Task[]> {
  const { tasks } = await api<{ tasks: Task[] }>('/tasks/mine', { signal });
  return tasks;
}

export interface CreateTaskInput {
  title: string;
  assigneeUserIds?: string[];
  classId?: string | null;
  dueAt?: string | null;
}

export async function createTask(input: CreateTaskInput): Promise<{ created: number }> {
  return api<{ created: number }>('/tasks', { method: 'POST', body: input });
}

export async function deleteTask(id: string): Promise<void> {
  await api(`/tasks/${id}`, { method: 'DELETE' });
}

export async function transitionTask(id: string, status: TaskStatus): Promise<void> {
  await api(`/tasks/${id}/transition`, { method: 'POST', body: { status } });
}

// ─── Notifications ─────────────────────────────────────────────────────────

export interface Notification {
  id: string;
  kind: string;
  payload: {
    titleKey?: string;
    bodyKey?: string;
    params?: Record<string, string | number>;
    link?: string;
  } & Record<string, unknown>;
  readAt: string | null;
  createdAt: string;
}

export async function listNotifications(
  signal?: AbortSignal,
): Promise<{ notifications: Notification[]; unreadCount: number }> {
  return api<{ notifications: Notification[]; unreadCount: number }>('/notifications', { signal });
}

export async function markNotificationsRead(ids?: string[]): Promise<void> {
  await api('/notifications/read', { method: 'POST', body: ids ? { ids } : {} });
}
